import {
    ChatHistoryLink,
    ChatHistoryViewAllRow,
} from "@/components/assistant-ui/ChatHistoryLink";
import { OwnedThreadListItem } from "@/components/assistant-ui/OwnedThreadListItem";
import { SharedThreadListItem } from "@/components/assistant-ui/SharedThreadListItem";
import { ThreadGroup } from "@/components/assistant-ui/ThreadGroup";
import { useThreadPartition } from "@/components/assistant-ui/useThreadPartition";
import { Button, buttonVariants } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useOwnedConversations } from "@/hooks/useOwnedConversations";
import { useSharedConversations } from "@/hooks/useSharedConversation";
import {
    CHAT_HISTORY_PATH,
    CHAT_HISTORY_SHARED_PATH,
    getChatHistoryTab,
} from "@/pages/frontend/chat/chatRoutes";
import { useThreadListLoadFailed } from "@/pages/frontend/chat/components/runtime-provider.list-status";
import { AuiIf, ThreadListPrimitive, useAui } from "@assistant-ui/react";
import { PlusIcon } from "lucide-react";
import type { FC } from "react";
import { Link, useLocation } from "react-router";

const CHATS_HISTORY = {
    to: CHAT_HISTORY_PATH,
    label: "View all chat history",
};

const SHARED_HISTORY = {
    to: CHAT_HISTORY_SHARED_PATH,
    label: "View all shared chat history",
};

export const ThreadList: FC = () => {
    const { pinnedIndices, sharedIndices, ownedIndices } = useThreadPartition();
    const ownedOverflows = (useOwnedConversations()?.pages ?? 0) > 1;
    const sharedOverflows = (useSharedConversations()?.pages ?? 0) > 1;
    const listFailed = useThreadListLoadFailed();
    // Groups stack back-to-back with no dividers — the headings + the root's
    // `gap-1` are the only separation between Pinned, Shared with me, and
    // Recents.

    return (
        <ThreadListPrimitive.Root className="aui-root aui-thread-list-root flex flex-col gap-1">
            <ThreadListNew />

            <AuiIf condition={(s) => s.threads.isLoading}>
                <ThreadGroup
                    title="Recents"
                    action={<ChatHistoryLink {...CHATS_HISTORY} />}
                >
                    <ThreadListSkeleton />
                </ThreadGroup>
            </AuiIf>

            <AuiIf condition={(s) => !s.threads.isLoading}>
                <ThreadGroup title="Pinned" hideWhenEmpty>
                    {pinnedIndices.map((i) => (
                        <ThreadListPrimitive.ItemByIndex
                            key={i}
                            index={i}
                            archived={false}
                            components={{
                                ThreadListItem: OwnedThreadListItem,
                            }}
                        />
                    ))}
                </ThreadGroup>

                <ThreadGroup
                    title="Shared with me"
                    hideWhenEmpty
                    action={<ChatHistoryLink {...SHARED_HISTORY} />}
                >
                    {sharedIndices.map((i) => (
                        <ThreadListPrimitive.ItemByIndex
                            key={i}
                            index={i}
                            archived={false}
                            components={{
                                ThreadListItem: SharedThreadListItem,
                            }}
                        />
                    ))}
                    {sharedOverflows && (
                        <ChatHistoryViewAllRow {...SHARED_HISTORY} />
                    )}
                </ThreadGroup>

                <ThreadGroup
                    title="Recents"
                    hideWhenEmpty={pinnedIndices.length === 0}
                    action={<ChatHistoryLink {...CHATS_HISTORY} />}
                >
                    {listFailed && <ThreadListLoadError />}
                    {ownedIndices.map((i) => (
                        <ThreadListPrimitive.ItemByIndex
                            key={i}
                            index={i}
                            archived={false}
                            components={{
                                ThreadListItem: OwnedThreadListItem,
                            }}
                        />
                    ))}
                    {ownedOverflows && (
                        <ChatHistoryViewAllRow {...CHATS_HISTORY} />
                    )}
                </ThreadGroup>
            </AuiIf>
        </ThreadListPrimitive.Root>
    );
};

// History pages run on a new main thread, which ThreadListPrimitive.New marks
// current; props passed here override its attributes.
const NOT_CURRENT = { "aria-current": undefined, "data-active": undefined };

const ThreadListNew: FC = () => {
    const onHistory = getChatHistoryTab(useLocation().pathname) !== null;

    return (
        <ThreadListPrimitive.New
            {...(onHistory ? NOT_CURRENT : {})}
            render={
                <Link
                    to={"/organizations/chat"}
                    className={buttonVariants({
                        className: "aui-thread-list-new h",
                    })}
                />
            }
        >
            <PlusIcon className="size-4" />
            New Chat
        </ThreadListPrimitive.New>
    );
};

const ThreadListLoadError: FC = () => {
    const aui = useAui();

    return (
        <div className="text-muted-foreground flex min-h-9 flex-wrap items-center gap-x-2 px-3 py-1.5 text-sm">
            <span>Couldn't load chats</span>
            <Button
                variant="link"
                size="sm"
                className="h-auto px-0"
                onClick={() => void aui.threads().getLoadThreadsPromise()}
            >
                Try again
            </Button>
        </div>
    );
};

const ThreadListSkeleton: FC = () => {
    return (
        <div className="flex flex-col gap-1">
            {Array.from({ length: 5 }, (_, i) => (
                <div
                    key={i}
                    role="status"
                    aria-label="Loading threads"
                    className="aui-thread-list-skeleton-wrapper flex h-9 items-center px-3"
                >
                    <Skeleton className="aui-thread-list-skeleton h-4 w-full" />
                </div>
            ))}
        </div>
    );
};
